import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../lib/supabase.js';
import { formatNumber } from '../../styles/tokens/formatters';
import WalletStageCard from './mobile/components/WalletStageCard.jsx';
import { subDays } from 'date-fns';
import SectionHeader from '../../components/ui/fancy/SectionHeader.jsx';

const ConversionesDashboard = () => {
  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);
  const navigate = useNavigate();

  const [counts, setCounts] = useState({
    vistas: 0,
    sesiones: 0,
    simulaciones: 0,
    contrataciones: 0,
    suscripciones: 0,
    certificados: 0
  });
  const [weeklyGrowth, setWeeklyGrowth] = useState({
    vistas: 0,
    sesiones: 0,
    simulaciones: 0,
    contrataciones: 0,
    suscripciones: 0,
    certificados: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) return;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const uaDataMobile = typeof navigator !== 'undefined' ? navigator.userAgentData?.mobile : false;
    const isMobile = Boolean(uaDataMobile) || /Android|iPhone|iPad|iPod/i.test(ua);
    if (isMobile) navigate('/conversiones/m', { replace: true });
  }, [session, navigate]);

  useEffect(() => {
    if (!session) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const today = new Date();
        const lastWeekStart = subDays(today, 7);
        const twoWeeksAgoStart = subDays(today, 14);

        const [vis, ses, sim, con, sus, cer] = await Promise.all([
          supabase.from('sesiones').select('id', { count: 'exact', head: true }).gte('created_at', lastWeekStart.toISOString()),
          supabase.from('sesiones_funnel').select('id', { count: 'exact', head: true }).eq('is_simulacion', true).gte('created_at', lastWeekStart.toISOString()),
          supabase.from('company_cotizaciones').select('id', { count: 'exact', head: true }).gte('created_at', lastWeekStart.toISOString()),
          supabase.from('company_contrataciones').select('id', { count: 'exact', head: true }).gte('created_at', lastWeekStart.toISOString()),
          supabase.from('company_suscripciones').select('id', { count: 'exact', head: true }).gte('created_at', lastWeekStart.toISOString()),
          supabase.from('company_certificados').select('id', { count: 'exact', head: true }).gte('created_at', lastWeekStart.toISOString())
        ]);

        if (vis.error) throw vis.error;
        if (ses.error) throw ses.error;
        if (sim.error) throw sim.error;
        if (con.error) throw con.error;
        if (sus.error) throw sus.error;
        if (cer.error) throw cer.error;

        setCounts({
          vistas: vis.count || 0,
          sesiones: ses.count || 0,
          simulaciones: sim.count || 0,
          contrataciones: con.count || 0,
          suscripciones: sus.count || 0,
          certificados: cer.count || 0
        });

        // Calculate Growth
        const getGrowth = async (table, withSimulacion = false) => {
           let thisWeekQuery = supabase
             .from(table)
             .select('id', { count: 'exact', head: true })
             .gte('created_at', lastWeekStart.toISOString());
           
           if (withSimulacion) {
             thisWeekQuery = thisWeekQuery.eq('is_simulacion', true);
           }

           const { count: thisWeekCount, error: err1 } = await thisWeekQuery;
           
           if (err1) return 0;

           let lastWeekQuery = supabase
             .from(table)
             .select('id', { count: 'exact', head: true })
             .gte('created_at', twoWeeksAgoStart.toISOString())
             .lt('created_at', lastWeekStart.toISOString());
            
           if (withSimulacion) {
             lastWeekQuery = lastWeekQuery.eq('is_simulacion', true);
           }

           const { count: lastWeekCount, error: err2 } = await lastWeekQuery;
            
            if (err2) return 0;
            
            const current = thisWeekCount || 0;
            const previous = lastWeekCount || 0;

            if (previous === 0) return current > 0 ? 100 : 0;
            return ((current - previous) / previous) * 100;
        };

        const [gVis, gSes, gSim, gCon, gSus, gCer] = await Promise.all([
            getGrowth('sesiones'),
            getGrowth('sesiones_funnel', true),
            getGrowth('company_cotizaciones'),
            getGrowth('company_contrataciones'),
            getGrowth('company_suscripciones'),
            getGrowth('company_certificados')
        ]);

        setWeeklyGrowth({
            vistas: gVis,
            sesiones: gSes,
            simulaciones: gSim,
            contrataciones: gCon,
            suscripciones: gSus,
            certificados: gCer
        });

      } catch (e) {
        setError(e?.message || 'Error cargando dashboard');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase]);

  const rateVistasToSesiones = counts.vistas > 0 ? (counts.sesiones / counts.vistas) * 100 : 0;
  const rateSesionesToSimulaciones = counts.sesiones > 0 ? (counts.simulaciones / counts.sesiones) * 100 : 0;
  const rateSimulacionesToContrataciones = counts.simulaciones > 0 ? (counts.contrataciones / counts.simulaciones) * 100 : 0;
  const rateContratacionesToSuscripciones = counts.contrataciones > 0 ? (counts.suscripciones / counts.contrataciones) * 100 : 0;
  const rateSuscripcionesToCertificados = counts.suscripciones > 0 ? (counts.certificados / counts.suscripciones) * 100 : 0;

  const trafficStages = useMemo(
    () => [
      {
        stageKey: 'vistas',
        title: 'Vistas',
        subtitle: 'Entrada',
        value: formatNumber(counts.vistas),
        growth: weeklyGrowth.vistas,
        to: '/conversiones/m/vistas'
      },
      {
        stageKey: 'sesiones',
        title: 'Sesiones',
        subtitle: '% sobre vistas',
        value: formatNumber(counts.sesiones),
        rateText: `${rateVistasToSesiones.toFixed(1)}%`,
        growth: weeklyGrowth.sesiones,
        to: '/conversiones/m/sesiones'
      },
      {
        stageKey: 'simulaciones',
        title: 'Simulaciones',
        subtitle: '% sobre sesiones',
        value: formatNumber(counts.simulaciones),
        rateText: `${rateSesionesToSimulaciones.toFixed(1)}%`,
        growth: weeklyGrowth.simulaciones,
        to: '/conversiones/m/simulaciones'
      }
    ],
    [counts, weeklyGrowth, rateVistasToSesiones, rateSesionesToSimulaciones]
  );

  const conversionStages = useMemo(
    () => [
      {
        stageKey: 'contrataciones',
        title: 'Contrataciones',
        subtitle: '% sobre simulaciones',
        value: formatNumber(counts.contrataciones),
        rateText: `${rateSimulacionesToContrataciones.toFixed(1)}%`,
        growth: weeklyGrowth.contrataciones,
        to: '/conversiones/m/contrataciones'
      },
      {
        stageKey: 'suscripciones',
        title: 'Suscripciones',
        subtitle: '% sobre contrataciones',
        value: formatNumber(counts.suscripciones),
        rateText: `${rateContratacionesToSuscripciones.toFixed(1)}%`,
        growth: weeklyGrowth.suscripciones,
        to: '/conversiones/m/suscripciones'
      },
      {
        stageKey: 'certificados',
        title: 'Certificados',
        subtitle: '% sobre suscripciones',
        value: formatNumber(counts.certificados),
        rateText: `${rateSuscripcionesToCertificados.toFixed(1)}%`,
        growth: weeklyGrowth.certificados,
        to: '/conversiones/m/certificados'
      }
    ],
    [counts, weeklyGrowth, rateSimulacionesToContrataciones, rateContratacionesToSuscripciones, rateSuscripcionesToCertificados]
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Conversiones</h1>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Cargando...</div>
      ) : error ? (
        <div className="text-sm text-[var(--yool-error)]">{error}</div>
      ) : (
        <div className="space-y-6">
          <SectionHeader title="Tráfico & Engagement" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {trafficStages.map((stage) => (
                <div key={stage.stageKey} className="transform transition-transform hover:-translate-y-1">
                  <WalletStageCard
                    stageKey={stage.stageKey}
                    title={stage.title}
                    subtitle={stage.subtitle}
                    value={stage.value}
                    rateText={stage.rateText}
                    growth={stage.growth}
                    isPrimary={false}
                    isExpanded={true}
                    instructionText="Ver registros"
                    onPress={() => navigate(stage.to)}
                  />
                </div>
             ))}
          </div>

          <SectionHeader title="Conversión & Ventas" className="mt-8 mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {conversionStages.map((stage) => (
                <div key={stage.stageKey} className="transform transition-transform hover:-translate-y-1">
                  <WalletStageCard
                    stageKey={stage.stageKey}
                    title={stage.title}
                    subtitle={stage.subtitle}
                    value={stage.value}
                    rateText={stage.rateText}
                    growth={stage.growth}
                    isPrimary={false}
                    isExpanded={true}
                    instructionText="Ver registros"
                    onPress={() => navigate(stage.to)}
                  />
                </div>
             ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionesDashboard;
